import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import styled from '@emotion/styled';
import FontAwesome from 'react-fontawesome';

import { groupReplies } from '~/utils/misc';
import PopupVideoPlayer from '~/App/Components/PopupVideoPlayer';
import { Reply, ReplyMedia, ReplyMediaStatus } from '~/types/models/reply';
import { assetUrl } from '~/helpers';
import { REPLY_BY_VIDEO_VIEWED } from '~/generated/AnalyticsTypes';
import analytics from '~/utils/Analytics';
import Linkify from '~/App/Components/Linkify';

const VideoPlayerContainer = styled.div`
  div {
    border-radius: 10px;
    bacgkround: none;
    video {
      border-radius: 10px;
    }
  }
`;

const PlayerBox = styled.div`
  background: black;
  position: relative;
  cursor: pointer;
  &:hover .pause-icon {
    opacity: 0.8;
  }
  video {
    flex-grow: 1;
  }
  width: 125px;
  height: 125px;
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  border-radius: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100%;
`;

const Thumbnail = styled.img`
  width: 125px;
  height: 125px;
  border-radius: 10px;
`;

const Loading = styled(FontAwesome)`
  font-size: 24px;
`;

const Error = styled(FontAwesome)`
  font-size: 24px;
`;

const CenterContainer = styled.div`
  margin: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 15px;
  color: #ffffff;
  width: 100%;
  font-weight: 600;
`;

const Controls = styled.div`
  background: transparent;
  width: 55px;
  height: 55px;
  padding: unset;
  position: absolute;
  bottom: 50%;
  right: 50%;
  transform: translate(50%, 50%);
  img {
    width: 55px;
    height: 55px;
  }
  .pause-icon {
    transition: opacity 2s;
    opacity: 0;
  }
`;

const StyledVideo = styled.video`
  width: 125px;
  height: 125px;
  object-fit: cover;
`;

const ListItem = styled.div`
  :last-child {
    margin-bottom: 10px;
  }
`;

const StyledCommentList = styled.div<
  Pick<CommentItemProps, 'yours' | 'chatBubbleWidth'>
>`
  background: #F8F8F8 0% 0% no-repeat padding-box;
  border-radius: 5px;
  word-break: break-word;
  opacity: 1;
  padding: 16px;
  margin-top: 15px;
  width: fit-content;
  min-width: ${(props) => props.chatBubbleWidth};
  &:last-child {
    margin-bottom: 10px;
  }

  ${(props) => {
    if (props.yours) {
      return `
        float: right;
        margin-left: 20px;
      `;
    }

    return `
      margin-right: 20px;
    `;
  }}}
`;

const StyledCommentItem = styled.div`
  border-left: unset !important;
  font-size: 16px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Author = styled.div<Pick<CommentItemProps, 'yours'>>`
  color: #27b3c1;
  opacity: 1;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  ${(props) => (props.yours ? 'color: #4A90E2;' : '')};
`;

const EmptyCommentListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  font: normal normal normal 14px/17px Lato-Bold;
  font-family: Lato;
  letter-spacing: 0px;
  color: #cccccc;
  opacity: 1;
  margin-top: 8px;
`;

const EmptyCommentReplyIcon = styled.img`
  width: 88px;
  height: 40px;
  margin-bottom: 16px;
`;

export type CommentListProps = {
  className?: string;
  comments: Reply[];
  children?: React.ReactChild;
  showTimestamps?: boolean;
  recipientName?: string;
  hideEmptyReplyMessage?: boolean;
  chatBubbleWidth?: string;
  viewedByRecipient?: boolean;
};

type CommentItemProps = Partial<Reply> &
  Pick<CommentListProps, 'chatBubbleWidth'> & {
    yours?: boolean;
  };

export interface ReplyVideoIconProps
  extends ReplyMedia,
    Pick<Reply, 'transcript'> {}

function ReplyVideoIcon({
  thumb_url,
  status,
  video_url,
  transcript,
}: ReplyVideoIconProps) {
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const isLoading =
    status !== ReplyMediaStatus.COMPLETE && status !== ReplyMediaStatus.ERROR;
  const isError = status === ReplyMediaStatus.ERROR;
  const isComplete = status === ReplyMediaStatus.COMPLETE;

  const togglePopup = useCallback(() => {
    if (!isLoading && !isError) {
      setIsPopUpOpen(true);

      void analytics.track(REPLY_BY_VIDEO_VIEWED, {
        url: window.location.href,
      });
    }
  }, [isLoading, isError]);

  return (
    <VideoPlayerContainer>
      <PopupVideoPlayer
        src={video_url}
        transcript={transcript}
        isOpen={isPopUpOpen}
        setOpen={setIsPopUpOpen}
      />
      <PlayerBox onClick={togglePopup}>
        {thumb_url ? (
          <Thumbnail src={thumb_url} />
        ) : (
          <StyledVideo>
            <source src={video_url} />
          </StyledVideo>
        )}
        {isComplete && (
          <Controls>
            <img src={assetUrl('assets/images/icons/ic_play_thick.png')} />
          </Controls>
        )}
        {(isLoading || isError) && (
          <OverlayContainer>
            <CenterContainer>
              <div style={{ textAlign: 'center' }}>
                {isLoading ? 'Video Processing' : "Couldn't load the video"}
              </div>
              {isLoading ? (
                <Loading name='spinner' className='spinning' />
              ) : (
                <Error name='exclamation-triangle' />
              )}
            </CenterContainer>
          </OverlayContainer>
        )}
      </PlayerBox>
    </VideoPlayerContainer>
  );
}
function CommentItem({ id, content, media, transcript }: CommentItemProps) {
  return (
    <StyledCommentItem key={id}>
      <div className='comment-text' />
      {!media && content && <Linkify className='comment-text' text={content} />}
      {media && <ReplyVideoIcon {...media} transcript={transcript} />}
    </StyledCommentItem>
  );
}

type EmptyCommentListProps = {
  author?: string;
};
function EmptyCommentList({ author }: EmptyCommentListProps) {
  return (
    <EmptyCommentListContainer>
      <EmptyCommentReplyIcon
        className='svg'
        src={assetUrl('assets/images/icons/message.svg')}
      />
      Send {author} a reply.
    </EmptyCommentListContainer>
  );
}

export default function CommentList({
  comments,
  children,
  className = '',
  showTimestamps = false,
  recipientName,
  hideEmptyReplyMessage = false,
  chatBubbleWidth = '70%',
  viewedByRecipient = false,
}: CommentListProps) {
  function renderThreads() {
    const threads = groupReplies(comments, showTimestamps);
    if (!threads.length && !hideEmptyReplyMessage) {
      return <EmptyCommentList author={recipientName} />;
    }

    return threads.map(({ author, replies, lastMessage }, i) => {
      const isSender = viewedByRecipient
        ? author.is_recipient
        : author.is_current_user;

      return (
        <ListItem key={`thread-${i}-${author.id}`}>
          <StyledCommentList
            className='comment-list'
            yours={isSender}
            chatBubbleWidth={chatBubbleWidth}
          >
            <Author yours={isSender}>{author.name}</Author>
            {replies.map((comment, i) => {
              return (
                <CommentItem
                  {...comment}
                  yours={isSender}
                  key={`comment-${i}`}
                />
              );
            })}
            {!showTimestamps || (
              <div className='comment-timestamp'>{lastMessage}</div>
            )}
          </StyledCommentList>
        </ListItem>
      );
    });
  }

  return (
    <Container className={cx('comment-list', className)}>
      {children}
      {renderThreads()}
    </Container>
  );
}
